// FLAGS: none
// Radix dialog that loads an expert's session types, languages and closed pairs for the form.
import { Dialog } from "radix-ui";
import { ListFilter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import {
  useExpertLanguageAvailabilityQuery,
  useUpdateExpertLanguageAvailability,
} from "@src/queries/language-availability";
import {
  psychologistLanguageTagName,
  sessionTypeTagName,
  useUserTagsQuery,
} from "@src/queries/tags";
import { withSnackbar, WithSnackbarProps } from "../SnackBarComponent";
import LanguageAvailabilityForm from "./LanguageAvailabilityForm";

interface DialogContentsProps extends WithSnackbarProps {
  expertId: string;
  onClose: () => void;
}

const DialogContents = ({
  expertId,
  onClose,
  snackbarShowMessage,
}: DialogContentsProps) => {
  const { t } = useTranslation();

  const { userTags: allExpertSessionTypes } = useUserTagsQuery(
    expertId,
    sessionTypeTagName,
  );
  const { userTags: expertLanguages } = useUserTagsQuery(
    expertId,
    psychologistLanguageTagName,
  );
  const { data: closedPairs, isLoading: closedPairsLoading } =
    useExpertLanguageAvailabilityQuery(expertId);
  const mutation = useUpdateExpertLanguageAvailability(expertId);

  const isLoading =
    !allExpertSessionTypes || !expertLanguages || closedPairsLoading;

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 py-3 pr-2 border-b border-solid border-slate-200">
        <div className="flex items-center gap-2">
          <ListFilter className="size-5 text-indigo-800" />
          <Dialog.Title className="text-lg font-semibold text-indigo-800 m-0">
            {t("LanguageAvailability.DialogTitle")}
          </Dialog.Title>
        </div>
        <Dialog.Close className="cursor-pointer border-none bg-transparent">
          <X className="size-5 text-slate-700" />
        </Dialog.Close>
      </div>
      {isLoading || !closedPairs ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <LanguageAvailabilityForm
          allExpertSessionTypes={allExpertSessionTypes ?? []}
          expertLanguages={expertLanguages ?? []}
          initialClosedPairs={closedPairs}
          mutation={mutation}
          onClose={onClose}
          snackbarShowMessage={snackbarShowMessage}
        />
      )}
    </>
  );
};

interface LanguageAvailabilityDialogProps extends WithSnackbarProps {
  open: boolean;
  onClose: () => void;
  expertId: string;
}

const LanguageAvailabilityDialog = ({
  open,
  onClose,
  expertId,
  snackbarShowMessage,
}: LanguageAvailabilityDialogProps) => (
  <Dialog.Root open={open} onOpenChange={(next: boolean) => !next && onClose()}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
      <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-[650px] h-[600px] -translate-x-1/2 -translate-y-1/2 flex flex-col bg-white rounded shadow-2xl outline-none overflow-hidden">
        <DialogContents
          key={expertId}
          expertId={expertId}
          onClose={onClose}
          snackbarShowMessage={snackbarShowMessage}
        />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

export default withSnackbar(LanguageAvailabilityDialog);
